import React, { useState } from "react";
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import "../css/Auth.css";

function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1);

  const handleRequestCode = async () => {
    try {
      setIsLoading(true);
      setError("");
      setSuccess("");
      
      await resetPassword({
        username: email,
      });
      
      setSuccess("Verification code has been sent to your email.");
      setStep(2);
    } catch (err) {
      console.error("Password reset request error:", err);
      setError(err.message || "An error occurred while requesting password reset");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      setIsLoading(true);
      setError("");
      setSuccess("");
      
      await confirmResetPassword({
        username: email,
        confirmationCode: verificationCode,
        newPassword: newPassword
      });
      
      setSuccess("Password has been reset successfully. You can now sign in with your new password.");
      setTimeout(() => {
        navigate("/signin");
      }, 2000);
    } catch (err) {
      console.error("Password reset error:", err);
      setError(err.message || "An error occurred while resetting password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <nav className="auth-nav">
        <div className="nav-content">
          <div className="logo" onClick={() => navigate('/')}>Equation Live</div>
        </div>
      </nav>

      <div className="auth-content">
        <div className="auth-box">
          <h1>Reset Password</h1>
          {step === 1 ? (
            <>
              <p className="auth-description">
                Enter your email address and we'll send you a verification code to reset your password.
              </p>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}
              <button 
                className="auth-button" 
                onClick={handleRequestCode}
                disabled={!email || isLoading}
              >
                {isLoading ? "Sending..." : "Send Verification Code"}
              </button>
            </>
          ) : (
            <>
              <p className="auth-description">
                Enter the verification code sent to your email and your new password.
              </p>
              <div className="form-group">
                <label htmlFor="verificationCode">Verification Code</label>
                <input
                  id="verificationCode"
                  type="text"
                  placeholder="Enter verification code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <div className="password-input-container">
                  <input
                    id="newPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}
              <button 
                className="auth-button" 
                onClick={handleResetPassword}
                disabled={!verificationCode || !newPassword || isLoading}
              >
                {isLoading ? "Resetting..." : "Reset Password"}
              </button>
            </>
          )}
          <p className="auth-switch">
            Remember your password?{" "}
            <button className="link-button" onClick={() => navigate("/signin")}>
              Sign In
            </button>
          </p>
        </div>
      </div>

      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Equation Live</h4>
            <p>Enhance your ChatGPT experience with our built-in equation editor and keyboard.</p>
          </div>
          <div className="footer-section">
            <ul>
              <li><a href="/support">Support</a></li>
              <li><a href="/privacy">Privacy</a></li>
              <li><a href="/terms">Terms</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Equation Live. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default ForgotPassword; 